import { get } from 'svelte/store';

import { user_data, user_settings } from 'src/stores/account/user';
import { devices } from 'src/stores/lists/devices';
import { app } from 'src/stores/session/native-bridge';
import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
import { applicationHostUrl } from 'src/helpers/account_helpers';
import { navigateToUrl } from 'src/components/Lists/list-helpers';
import { paypal, paypalTest, postReq, get_user_settings } from 'src/services/http';
import { isDevServerUser } from 'src/helpers/user_server_conditions';
import { isCameraDevice, shellyId16 } from 'src/helpers/interface_helpers';
import { showNewLayout } from 'src/stores/toDelete/new_layout';
import {
	CameraCloudStorageSubscriptionStatus,
	type CameraCloudStorageActivePlan,
	type CameraCloudStoragePlan,
	type CameraCloudStoragePlanTier,
	type CameraCloudStoragePlanType,
	type CameraCloudStorageSlot,
	type CameraSubscribeResponse,
	type CameraUnsubscribeResponse,
	type CameraUpgradePreviewResponse,
	type SetCameraListResponse,
	type UserCameraSlotsSettings,
	type UserCameraSubscriptionSettings,
} from 'src/types/CameraCloudStorage';

/**
 * Camera Cloud Storage colour palette (purple gradient) used across the
 * section banner, plan cards and the manage modal. Kept in one place so
 * future design-system tokens can replace it.
 */
export const CAMERA_CLOUD_STORAGE_GRADIENT_START = '#D56CF8';
export const CAMERA_CLOUD_STORAGE_GRADIENT_END = '#893BF2';
export const CAMERA_CLOUD_STORAGE_GRADIENT = `linear-gradient(135deg, ${CAMERA_CLOUD_STORAGE_GRADIENT_START} 0%, ${CAMERA_CLOUD_STORAGE_GRADIENT_END} 100%)`;

/**
 * Catalogue of the camera plans that ship in v1 of BE-924. Yearly
 * variants exist server-side but are rejected by the API, so they are
 * intentionally not surfaced here.
 *
 * The `planType` value is the exact identifier the BE expects on the
 * subscribe / upgrade-preview / upgrade endpoints (BE-924 §2 & §4).
 */
export const CAMERA_CLOUD_STORAGE_PLANS: readonly CameraCloudStoragePlan[] = [
	{
		tier: 3,
		planType: '3_cameras_monthly',
		priceMonthly: 6.99,
	},
	{
		tier: 6,
		planType: '6_cameras_monthly',
		priceMonthly: 10.99,
	},
	{
		tier: 10,
		planType: '10_cameras_monthly',
		priceMonthly: 14.99,
	},
] as const;

/**
 * Premium users get one Camera Cloud Storage slot included with the
 * Home (Premium) subscription. Product rule, not exposed by BE-924;
 * revisit if the backend formalises the policy.
 */
export const PREMIUM_INCLUDED_CAMERA_SLOTS = 1;

/**
 * DOM id of the "Choose Add On" plans grid inside the Camera Cloud
 * Storage section. Used by the active-plan info card (FE-1719) so the
 * parent page can scroll the user directly to the plans grid when the
 * upgrade has more than one possible target tier, and as the scroll
 * anchor for the deep-link that opens the Subscriptions page directly
 * on the plans grid (`#/settings/premium?ccs_scroll=1`).
 */
export const CAMERA_CLOUD_STORAGE_PLANS_SECTION_ID = 'camera-cloud-storage-plans';

/**
 * DOM id of the "Storage Settings" button inside the Camera Cloud
 * Storage active-plan banner. The promo banner shown in the camera
 * side-panel tabs uses this id to scroll the user directly to the
 * button after pushing them to `/subscriptions` (variant B —
 * premium-or-CCS users who haven't yet assigned a camera to a slot).
 */
export const CAMERA_CLOUD_STORAGE_STORAGE_SETTINGS_BUTTON_ID = 'camera-cloud-storage-settings-button';

/** Query-string flag set by the PayPal return URL. */
export const CAMERA_CLOUD_STORAGE_RETURN_PARAM = 'ccs_state';

/**
 * Query-string flag that deep-links straight to the Camera Cloud
 * Storage "Choose Add On" plans grid and scrolls it into view. Set it
 * on the Subscriptions route (`#/settings/premium?ccs_scroll=1`) to
 * land the user on the plans grid; the flag is consumed and cleared on
 * mount.
 */
export const CAMERA_CLOUD_STORAGE_SCROLL_PARAM = 'ccs_scroll';

/**
 * Shelly online shop URL the "No camera device" modal sends the user
 * to when they try to subscribe/upgrade/renew a Camera Cloud Storage
 * plan without owning a camera device. Centralised so the link can be
 * swapped in one place and stays consistent across the two call sites
 * (`CameraCloudStorageSection` and `CameraCloudStoragePlanInfoCard`).
 */
export const CAMERA_CLOUD_STORAGE_SHOP_URL = 'https://www.shelly.com/collections/all-products';

/**
 * Whether the current account owns at least one camera device. Used
 * to gate the Camera Cloud Storage subscribe / upgrade / renew flows
 * — the BE rejects subscriptions for accounts with no cameras, and
 * we want to surface a friendly "Buy a camera" prompt instead of a
 * raw API error. Reads `$devices` synchronously so it can be invoked
 * inside click handlers without a reactive subscription.
 */
export function accountHasCameraDevice(): boolean {
	const all = get(devices) ?? {};
	return Object.values(all).some((device) => isCameraDevice(device));
}

/** Number of Cloud Storage slots included with Home (Premium); zero when not premium. */
export function getPremiumIncludedSlotCount(isPremium: boolean): number {
	return isPremium ? PREMIUM_INCLUDED_CAMERA_SLOTS : 0;
}

/**
 * Total number of Cloud Storage slots available to the user, combining
 * the Premium-included slots with the active Camera Cloud Storage plan
 * tier (if any).
 */
export function getTotalSlotCount(
	activePlan: CameraCloudStorageActivePlan | null,
	isPremium: boolean,
): number {
	const fromPlan = activePlan?.tier ?? 0;
	return fromPlan + getPremiumIncludedSlotCount(isPremium);
}

/**
 * Build the ordered list of slots that the user currently has access
 * to. Premium-included slot(s) come first, then the slots that belong
 * to the active Camera Cloud Storage plan.
 */
export function buildSlots(
	activePlan: CameraCloudStorageActivePlan | null,
	isPremium: boolean,
	assignments: Record<number, string | null>,
): CameraCloudStorageSlot[] {
	const slots: CameraCloudStorageSlot[] = [];
	const premiumSlots = getPremiumIncludedSlotCount(isPremium);

	for (let i = 0; i < premiumSlots; i++) {
		slots.push({ index: i, source: 'premium', cameraId: assignments[i] ?? null });
	}

	const planSlots = activePlan?.tier ?? 0;
	for (let i = 0; i < planSlots; i++) {
		const slotIndex = premiumSlots + i;
		slots.push({
			index: slotIndex,
			source: 'subscription',
			cameraId: assignments[slotIndex] ?? null,
		});
	}

	return slots;
}

export function getOccupiedCameraIds(assignments: Record<number, string | null>): Set<string> {
	const ids = new Set<string>();
	for (const value of Object.values(assignments)) {
		if (value) ids.add(value);
	}
	return ids;
}

export function getPlanByTier(tier: CameraCloudStoragePlanTier): CameraCloudStoragePlan | undefined {
	return CAMERA_CLOUD_STORAGE_PLANS.find((p) => p.tier === tier);
}

export function getPlanByPlanType(
	planType: CameraCloudStoragePlanType,
): CameraCloudStoragePlan | undefined {
	return CAMERA_CLOUD_STORAGE_PLANS.find((p) => p.planType === planType);
}

export function planTypeToTier(
	planType: CameraCloudStoragePlanType | undefined,
): CameraCloudStoragePlanTier | undefined {
	return planType ? getPlanByPlanType(planType)?.tier : undefined;
}

export type CameraCloudStoragePlanCardAction = 'subscribe' | 'active' | 'upgrade' | 'disabled';

/**
 * Whether a plan tier is subscribe / active / upgrade / disabled for the current subscription.
 * Mirrors BE-924 §3 (no downgrade).
 */
export function getPlanCardAction(
	plan: CameraCloudStoragePlan,
	activePlan: CameraCloudStorageActivePlan | null,
): CameraCloudStoragePlanCardAction {
	if (!activePlan || activePlan.status !== CameraCloudStorageSubscriptionStatus.Active) {
		return 'subscribe';
	}
	if (plan.tier === activePlan.tier) return 'active';
	if (plan.tier < activePlan.tier) return 'disabled';
	return 'upgrade';
}

/**
 * Pick the `paypal` http client that targets the user's environment.
 * Mirrors the dev/prod split used by the legacy premium subscribe flow.
 */
function getPaypalClient() {
	return isDevServerUser(['repo', 'nb']) ? paypalTest : paypal;
}

/**
 * Build the absolute `x-shelly-redirect` URL the BE forwards into the
 * PayPal `return_url`. The user lands back on the Subscriptions page
 * with `?ccs_state=1` so the FE can refresh status and open the
 * post-subscribe wizard.
 */
function buildReturnUrl(): string {
	return applicationHostUrl(
		import.meta.env.VITE_APP_BUCKET || window.location.host,
		'/#/settings/premium?ccs_state=1',
	);
}

/**
 * Redirect the browser to PayPal's approval URL. Mobile shells go
 * through `navigateToUrl` so the inner web-view stays consistent with
 * the legacy premium flow.
 */
function redirectToPaypal(link: string): void {
	if (get(app)) {
		navigateToUrl(link);
	} else {
		window.location.href = link;
	}
}

/**
 * Map raw BE errors (BE-924 §4.1 / §4.3 / §5) into translation keys
 * we can hand to `$format`. Returns the key plus a sensible English
 * default so callers don't need their own fallback table.
 */
export function translateCameraSubscribeError(
	res: Pick<CameraSubscribeResponse, 'error' | 'error_code'>,
): { key: string; default: string } {
	const code = String(res.error_code ?? '');
	if (code === '0x0003' || /downgrade/i.test(res.error ?? '')) {
		return {
			key: 'toast_camera_cloud_storage_downgrade_not_allowed',
			default: 'You cannot switch to a lower Camera Cloud Storage plan.',
		};
	}
	if (code === '0x0001') {
		return {
			key: 'toast_camera_cloud_storage_upgrade_24h',
			default: 'PayPal is still processing the previous change. Please try again in 24 hours.',
		};
	}
	if (/already have an active camera/i.test(res.error ?? '')) {
		return {
			key: 'toast_camera_cloud_storage_already_active',
			default: 'You already have an active Camera Cloud Storage subscription.',
		};
	}
	if (/not premium|cannot find/i.test(res.error ?? '')) {
		return {
			key: 'toast_camera_cloud_storage_no_active_plan',
			default: 'No active Camera Cloud Storage subscription was found to upgrade.',
		};
	}
	if (/missing invoice data/i.test(res.error ?? '')) {
		return {
			key: CAMERA_CLOUD_STORAGE_MISSING_INVOICE_KEY,
			default: 'Missing invoice data. Please fill in your invoice details first.',
		};
	}
	return {
		key: 'toast_camera_cloud_storage_subscribe_failed',
		default: 'Failed to subscribe to Camera Cloud Storage.',
	};
}

/** Error key returned when the BE rejects a subscribe/upgrade because invoice data is missing. */
export const CAMERA_CLOUD_STORAGE_MISSING_INVOICE_KEY =
	'toast_camera_cloud_storage_missing_invoice_data';

/** Extract the `error` string from an Axios-like rejected response (400/4xx). */
function extractAxiosErrorMessage(err: unknown): string | undefined {
	if (typeof err === 'object' && err !== null) {
		const axiosErr = err as { response?: { data?: { error?: string } } };
		if (typeof axiosErr.response?.data?.error === 'string') {
			return axiosErr.response.data.error;
		}
	}
	return undefined;
}

export interface SubscribeCameraCloudStorageResult {
	subscriptionId?: string;
	link?: string;
	error?: { key: string; default: string };
}

/**
 * Start a brand-new Camera Cloud Storage subscription (BE-924 §4.1).
 * On success (HTTP 202) the helper redirects the user to PayPal's
 * approval URL. Once the user finishes there, PayPal will redirect
 * back to `buildReturnUrl()` and the FE refreshes via
 * `refreshCameraSubscriptionFromSettings`.
 */
export async function subscribeCameraCloudStorage(
	tier: CameraCloudStoragePlanTier,
): Promise<SubscribeCameraCloudStorageResult> {
	const plan = getPlanByTier(tier);
	if (!plan) {
		return {
			error: {
				key: 'toast_camera_cloud_storage_subscribe_failed',
				default: `Unknown Camera Cloud Storage plan tier: ${tier}`,
			},
		};
	}

	try {
		const client = getPaypalClient();
		const res = (await client.post(
			`/api/subscribe/monthly/${plan.planType}`,
			{},
			{ headers: { 'x-shelly-redirect': buildReturnUrl() } },
		)) as { data?: CameraSubscribeResponse } | CameraSubscribeResponse;

		const data: CameraSubscribeResponse =
			(res as { data?: CameraSubscribeResponse })?.data ?? (res as CameraSubscribeResponse) ?? {};

		if (data.link) {
			redirectToPaypal(data.link);
			return { subscriptionId: data.subscription_id, link: data.link };
		}

		return { error: translateCameraSubscribeError(data) };
	} catch (err) {
		console.error('[camera-cloud-storage] subscribe failed', err);
		const beError = extractAxiosErrorMessage(err);
		return { error: translateCameraSubscribeError({ error: beError }) };
	}
}

/**
 * Camera-family upgrade (BE-924 §4.3). The BE creates a compensating
 * coupon for the unused remainder of the current plan, then asks
 * PayPal to start a new subscription whose `start_time` equals the
 * coupon's `end_time`. Same redirect contract as
 * `subscribeCameraCloudStorage`.
 */
export async function upgradeCameraCloudStorage(
	targetTier: CameraCloudStoragePlanTier,
): Promise<SubscribeCameraCloudStorageResult> {
	const plan = getPlanByTier(targetTier);
	if (!plan) {
		return {
			error: {
				key: 'toast_camera_cloud_storage_subscribe_failed',
				default: `Unknown Camera Cloud Storage plan tier: ${targetTier}`,
			},
		};
	}

	try {
		const client = getPaypalClient();
		const res = (await client.post(
			`/api/subscribe/upgrade/${plan.planType}`,
			{},
			{ headers: { 'x-shelly-redirect': buildReturnUrl() } },
		)) as { data?: CameraSubscribeResponse } | CameraSubscribeResponse;

		const data: CameraSubscribeResponse =
			(res as { data?: CameraSubscribeResponse })?.data ?? (res as CameraSubscribeResponse) ?? {};

		if (data.link) {
			redirectToPaypal(data.link);
			return { subscriptionId: data.subscription_id, link: data.link };
		}

		return { error: translateCameraSubscribeError(data) };
	} catch (err) {
		console.error('[camera-cloud-storage] upgrade failed', err);
		const beError = extractAxiosErrorMessage(err);
		return { error: translateCameraSubscribeError({ error: beError }) };
	}
}

export interface CameraUpgradePreviewResult {
	preview?: CameraUpgradePreviewResponse;
	error?: { key: string; default: string };
}

/**
 * Side-effect-free preview of a camera upgrade (BE-924 §4.2). Per the
 * spec the FE must call this before executing an upgrade so it can
 * display the prorated `compensating_days` and the new plan price.
 */
export async function getCameraUpgradePreview(
	targetTier: CameraCloudStoragePlanTier,
): Promise<CameraUpgradePreviewResult> {
	const plan = getPlanByTier(targetTier);
	if (!plan) {
		return {
			error: {
				key: 'toast_camera_cloud_storage_subscribe_failed',
				default: `Unknown Camera Cloud Storage plan tier: ${targetTier}`,
			},
		};
	}

	try {
		const client = getPaypalClient();
		const res = (await client.get(`/api/subscribe/upgrade-preview/${plan.planType}`)) as
			| CameraUpgradePreviewResponse
			| { data?: CameraUpgradePreviewResponse };
		const preview =
			(res as { data?: CameraUpgradePreviewResponse })?.data ??
			(res as CameraUpgradePreviewResponse);
		return { preview };
	} catch (err) {
		console.error('[camera-cloud-storage] upgrade-preview failed', err);
		return {
			error: {
				key: 'toast_camera_cloud_storage_subscribe_failed',
				default: 'Failed to load upgrade details.',
			},
		};
	}
}

export interface UnsubscribeCameraCloudStorageResult {
	ok?: boolean;
	error?: string;
}

/**
 * Cancel the camera subscription (BE-924 §4.5). Premium is NOT
 * affected — the BE keeps the two flags fully independent. The new
 * `POST /api/unsubscribe/camera` endpoint returns `{ camera: 0 | 1 }`;
 * we treat `0` as a confirmed cancellation.
 */
export async function unsubscribeCameraCloudStorage(): Promise<UnsubscribeCameraCloudStorageResult> {
	try {
		const client = getPaypalClient();
		const res = (await client.post('/api/unsubscribe/camera', {})) as
			| CameraUnsubscribeResponse
			| { data?: CameraUnsubscribeResponse };
		const data =
			(res as { data?: CameraUnsubscribeResponse })?.data ??
			(res as CameraUnsubscribeResponse) ??
			{};

		if (data.error) {
			return { error: data.error };
		}

		camera_cloud_storage.updateActivePlanStatus(CameraCloudStorageSubscriptionStatus.Cancelled);
		return { ok: true };
	} catch (err) {
		console.error('[camera-cloud-storage] unsubscribe failed', err);
		return { error: String(err) };
	}
}

/**
 * Map the `subscription_status` string returned by BE-924 to the
 * FE-side enum. Anything we don't recognise falls back to Unknown so
 * the UI can still render but treats it as "not active".
 */
function mapSubscriptionStatus(raw?: string): CameraCloudStorageSubscriptionStatus {
	switch (raw) {
		case 'ACTIVE':
			return CameraCloudStorageSubscriptionStatus.Active;
		case 'CANCELLED':
			return CameraCloudStorageSubscriptionStatus.Cancelled;
		case 'SUSPENDED':
			return CameraCloudStorageSubscriptionStatus.Suspended;
		default:
			return CameraCloudStorageSubscriptionStatus.Unknown;
	}
}

/**
 * Convert the `camera_subscription` block from `user_settings` into
 * the FE-side `activePlan` representation. Returns `null` when the
 * block is absent or marked `is_active: false` — both signal that the
 * user does not own an active camera subscription.
 */
export function buildActivePlanFromSettings(
	camera: UserCameraSubscriptionSettings | null | undefined,
): CameraCloudStorageActivePlan | null {
	if (!camera || !camera.is_active) return null;
	const tier = planTypeToTier(camera.plan_type);
	if (!tier) return null;
	return {
		tier,
		planType: camera.plan_type,
		status: mapSubscriptionStatus(camera.status),
	};
}

/**
 * Read the `camera_slots` block from `user_settings`. Defaults to
 * `{ free: 0, all: 0 }` when the BE has not yet populated the field
 * (older accounts, mid-migration, etc.).
 */
export function getSlotCountsFromSettings(
	settings: { camera_slots?: UserCameraSlotsSettings } | null | undefined,
): UserCameraSlotsSettings {
	const raw = settings?.camera_slots;
	return {
		free: typeof raw?.free === 'number' ? raw.free : 0,
		all: typeof raw?.all === 'number' ? raw.all : 0,
	};
}

/**
 * Map the BE's `camera_list` SET into the FE-side
 * `slotIndex -> cameraId` record. The BE stores `c:{email}:list` as
 * an *unordered* set, so the slot order itself is a FE display
 * concern: we drop the ids into slot 0, 1, 2 … in the order they
 * arrive. `totalSlots` caps the assignment so a stale list that
 * exceeds the current pool (e.g. plan downgraded server-side) does
 * not over-populate the UI.
 *
 * The backend stores decimal device IDs but `$devices` is keyed by
 * hex IDs. We normalise here via `shellyId16` so lookups, occupied
 * checks, and picker matching all use the same format as `device.id`.
 *
 * Returns an empty record when `camera_list` is absent — this is the
 * common case for accounts that have not yet assigned any camera.
 */
export function buildSlotAssignmentsFromCameraList(
	cameraList: readonly string[] | null | undefined,
	totalSlots: number,
): Record<number, string> {
	const assignments: Record<number, string> = {};
	if (!cameraList || cameraList.length === 0 || totalSlots <= 0) return assignments;
	const limit = Math.min(cameraList.length, totalSlots);
	for (let i = 0; i < limit; i++) {
		const id = cameraList[i];
		if (id) assignments[i] = shellyId16(String(id));
	}
	return assignments;
}

/**
 * Hydrate the `camera_cloud_storage` store from the supplied
 * `user_settings.settings` payload. Per Spas's spec
 * `POST /user/get_settings` is the source of truth for the camera
 * subscription state (no separate `/api/subscriptions` call) and for
 * slot assignments (via `camera_list`). The local store is rewritten
 * from scratch on every call so account switches automatically
 * discard stale per-account data — there is no longer any
 * cross-profile localStorage leak.
 */
export function hydrateFromUserSettings(
	settings:
		| {
				camera_subscription?: UserCameraSubscriptionSettings;
				camera_slots?: UserCameraSlotsSettings;
				camera_list?: readonly string[];
		  }
		| null
		| undefined,
): CameraCloudStorageActivePlan | null {
	const activePlan = buildActivePlanFromSettings(settings?.camera_subscription);
	camera_cloud_storage.setActivePlan(activePlan);

	const totalSlots = getSlotCountsFromSettings(settings).all;
	const assignments = buildSlotAssignmentsFromCameraList(settings?.camera_list, totalSlots);
	camera_cloud_storage.setAssignments(assignments);

	return activePlan;
}

/**
 * Refresh `user_settings` via `POST /user/get_settings` and apply the
 * camera-related fields to our local store. Used after the PayPal
 * return so the page reflects the BE-side subscription flip without a
 * hard reload.
 */
export async function refreshCameraSubscriptionFromSettings(): Promise<CameraCloudStorageActivePlan | null> {
	const settings = await get_user_settings();
	return hydrateFromUserSettings(settings ?? get(user_settings));
}

/**
 * Derive the active camera plan's next-billing date from
 * `GET /api/subscribe/upgrade-preview/:plantype` (BE-924 §4.2) and
 * push it into the `camera_cloud_storage` store. The
 * `user_settings.camera_subscription` block is intentionally
 * date-less per Spas's spec, so this helper bridges the gap so the
 * "UPCOMING PAYMENT / ACTIVE UNTIL" label on the plan-info card can
 * actually render a date (FE-1706).
 *
 * The preview endpoint requires a *target* plantype. Even when the
 * BE answers `upgrade.is_allowed: false` (e.g. we hit the top tier
 * and have to ask for a "downgrade"), the `current_plan` block —
 * which is what we read — is still populated with the right
 * `remaining_days`. We therefore pick any other tier in the catalogue
 * just to satisfy the URL.
 *
 * Resolves to the ISO string we set on the store, or `undefined` when
 * the plan is missing/inactive or the BE could not return a usable
 * `remaining_days` value (we leave the existing date untouched in
 * that case).
 */
export async function refreshCameraNextBillingTime(): Promise<string | undefined> {
	const state = get(camera_cloud_storage);
	const activePlan = state.activePlan;
	if (!activePlan || activePlan.status !== CameraCloudStorageSubscriptionStatus.Active) {
		return undefined;
	}

	const otherTier = CAMERA_CLOUD_STORAGE_PLANS.find((p) => p.tier !== activePlan.tier)?.tier;
	if (!otherTier) return undefined;

	const result = await getCameraUpgradePreview(otherTier);
	const remainingDays = result.preview?.current_plan?.remaining_days;
	if (typeof remainingDays !== 'number' || remainingDays <= 0) return undefined;

	const nextBillingMs = Date.now() + remainingDays * 86_400_000;
	const iso = new Date(nextBillingMs).toISOString();
	camera_cloud_storage.setNextBillingTime(iso);
	return iso;
}

/** Result of `POST /user/set_camera_list` (success or mapped translation error). */
export interface SetCameraSlotListResult {
	ok?: boolean;
	error?: {
		key: string;
		default: string;
		/** Offending device ids for `not_a_camera`. */
		ids?: string[];
	};
}

/**
 * Translation lookup for `POST /user/set_camera_list` errors. The
 * endpoint returns one of: `no_devices`, `invalid_devices`,
 * `camera_slots_exceeded`, `not_a_camera` (the last carries
 * `data: { ids }` pointing at the offending device(s)).
 */
function translateSetCameraListError(res: SetCameraListResponse): {
	key: string;
	default: string;
} {
	switch (res.error) {
		case 'no_devices':
			return {
				key: 'toast_camera_cloud_storage_set_list_no_devices',
				default: 'Select at least one camera to assign to your slots.',
			};
		case 'invalid_devices':
			return {
				key: 'toast_camera_cloud_storage_set_list_invalid_devices',
				default: 'One or more of the selected devices are invalid.',
			};
		case 'camera_slots_exceeded':
			return {
				key: 'toast_camera_cloud_storage_set_list_slots_exceeded',
				default: 'You picked more cameras than your available Cloud Storage slots.',
			};
		case 'not_a_camera':
			return {
				key: 'toast_camera_cloud_storage_set_list_not_a_camera',
				default: 'One or more of the selected devices are not cameras.',
			};
		default:
			return {
				key: 'toast_camera_cloud_storage_set_list_failed',
				default: 'Failed to update the Camera Cloud Storage slot list.',
			};
	}
}

/**
 * Replace the per-account camera-slot list (`c:{email}:list` in the
 * BE) with the supplied unordered set of device ids. Mirrors Spas's
 * contract:
 *
 *   POST /user/set_camera_list
 *   Authorization: Bearer <token>
 *   devices=["12345678901","987654321"]   (decimal device ids only)
 *
 * The BE validates that the count is within `camera_slots.all` and
 * that each id resolves to a `camera`-family device. On success we
 * re-fetch `user_settings` so the new `camera_slots.free` count is
 * picked up by the rest of the UI.
 */
export async function setCameraSlotList(deviceIds: readonly string[]): Promise<SetCameraSlotListResult> {
	try {
		const ids = Array.from(deviceIds).filter(Boolean);
		const res = (await postReq('/user/set_camera_list', {
			devices: JSON.stringify(ids),
		})) as SetCameraListResponse;

		if (res?.isok === false || res?.error) {
			const translated = translateSetCameraListError(res);
			return {
				error: {
					...translated,
					ids: res?.data?.ids,
				},
			};
		}

		await get_user_settings();
		return { ok: true };
	} catch (err) {
		console.error('[camera-cloud-storage] set_camera_list failed', err);
		return {
			error: {
				key: 'toast_camera_cloud_storage_set_list_failed',
				default: 'Failed to update the Camera Cloud Storage slot list.',
			},
		};
	}
}

/**
 * Convenience: is the current user eligible to see the Camera Cloud
 * Storage section at all? Today every signed-in user can see it
 * (Premium users get 1 free slot, non-Premium users see the marketing
 * state). Kept as a helper to centralise the rule for future tweaks.
 */
export function shouldShowCameraCloudStorageSection(): boolean {
	return !!get(user_data)?.email;
}
